import { useState } from "react";
import {
  Modal,
  Box,
  Button,
  Slider,
  Switch,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
} from "@mui/material";
import SilviculturePreview, {
  BASE_CH,
  BASE_CBD,
  cbdNorm,
  silvicultureResult,
  StandShape,
} from "./SilviculturePreview";

/**
 * Silviculture Lab — a bespoke editor for the Silviculture treatment, modelled
 * on the "Tree encoding lab" prototype. Three panels:
 *   • controls      — thinning (stems removed, surface fuel, slash) + pruning
 *   • cross-section — annotated treated tree with CH/CBH dimension lines
 *   • live 3D       — draggable treated stand + derived readouts
 *
 * A single thinning slider drives both elevated fuel reduction and canopy bulk
 * density in the sim (removing stems reduces both); pruning raises CBH. The
 * vegetation-type toggle sets the preview context: Vesta and Southern pine
 * carry no CBD attribute, so for those the lab notes that thinning acts on
 * fuel loads only. The treatment itself applies per-cell — each fuel model
 * picks up the levers it supports.
 */

type VegContext = "pine" | "southernPine" | "vesta" | "scottBurgan";

const VEG_CONTEXTS: Record<
  VegContext,
  { label: string; shape: StandShape; hasCBD: boolean }
> = {
  pine: { label: "Pine", shape: "conifer", hasCBD: true },
  southernPine: { label: "Southern pine", shape: "conifer", hasCBD: false },
  vesta: { label: "Eucalypt (Vesta)", shape: "broadleaf", hasCBD: false },
  scottBurgan: { label: "Scott & Burgan", shape: "conifer", hasCBD: true },
};

interface SilvicultureLabModalProps {
  open: boolean;
  onClose: () => void;
  thinningPct: number;
  setThinningPct: (v: number) => void;
  surfaceFuelReductionPct: number;
  setSurfaceFuelReductionPct: (v: number) => void;
  removeSlash: boolean;
  setRemoveSlash: (v: boolean) => void;
  cbhRaiseM: number;
  setCbhRaiseM: (v: number) => void;
}

const ACCENT = "#00d4ad";
const TEXT_2 = "rgba(255,255,255,0.7)";
const TEXT_3 = "rgba(255,255,255,0.5)";
const BORDER = "rgba(255,255,255,0.1)";

const sliderSx = {
  color: ACCENT,
  "& .MuiSlider-rail": { color: "rgba(255,255,255,0.18)" },
};

// ---------------------------------------------------------------------------
// Annotated 2D cross-section of the treated tree, with CH/CBH dimension lines.
// Crown size uses the baseline CBD — thinning removes stems rather than
// reshaping individual crowns.
// ---------------------------------------------------------------------------
function crownRadiusM(crownLen: number, cbd: number, shape: StandShape) {
  const cbdN = cbdNorm(cbd);
  return shape === "broadleaf"
    ? Math.max(1.0, crownLen * 0.5 * (0.6 + 0.85 * cbdN))
    : Math.max(0.7, crownLen * 0.26 * (0.6 + 0.9 * cbdN));
}

function CrossSection({
  afterCH,
  afterCBH,
  shape,
}: {
  afterCH: number;
  afterCBH: number;
  shape: StandShape;
}) {
  const W = 250;
  const H = 360;
  const groundY = 320;
  const topPad = 22;
  const S = (groundY - topPad) / BASE_CH; // px per metre (baseline fills height)
  const cx = 128;

  const crownLen = Math.max(afterCH - afterCBH, 0.4);
  const rPx = crownRadiusM(crownLen, BASE_CBD, shape) * S;
  const topY = groundY - afterCH * S;
  const baseY = groundY - afterCBH * S;
  const trunkW = Math.max(3, (0.13 + 0.014 * afterCH) * 2 * S);
  const cbdN = cbdNorm(BASE_CBD);
  const fill =
    shape === "broadleaf"
      ? `rgb(${Math.round(181 - 130 * cbdN)},${Math.round(209 - 119 * cbdN)},${Math.round(139 - 95 * cbdN)})`
      : `rgb(${Math.round(156 - 139 * cbdN)},${Math.round(192 - 112 * cbdN)},${Math.round(138 - 96 * cbdN)})`;
  const stroke = "rgba(255,255,255,0.35)";

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {/* ground */}
      <line x1="10" y1={groundY} x2={W - 10} y2={groundY} stroke="rgba(255,255,255,0.3)" />
      <text x={W - 10} y={groundY + 14} fill={TEXT_3} fontSize="9" textAnchor="end">
        ground
      </text>
      {/* treated tree */}
      <rect
        x={cx - trunkW / 2}
        y={baseY}
        width={trunkW}
        height={groundY - baseY}
        fill="#6b4a32"
      />
      {shape === "broadleaf" ? (
        <ellipse
          cx={cx}
          cy={(topY + baseY) / 2}
          rx={rPx}
          ry={(baseY - topY) / 2}
          fill={fill}
          stroke={stroke}
        />
      ) : (
        <polygon
          points={`${cx},${topY} ${cx - rPx},${baseY} ${cx + rPx},${baseY}`}
          fill={fill}
          stroke={stroke}
        />
      )}
      {/* CH dimension line (right) */}
      <line x1={W - 26} y1={topY} x2={W - 26} y2={groundY} stroke={ACCENT} strokeWidth="1.5" />
      <line x1={W - 30} y1={topY} x2={W - 22} y2={topY} stroke={ACCENT} />
      <line x1={W - 30} y1={groundY} x2={W - 22} y2={groundY} stroke={ACCENT} />
      <text
        x={W - 18}
        y={(topY + groundY) / 2}
        fill={ACCENT}
        fontSize="10"
        transform={`rotate(90 ${W - 18} ${(topY + groundY) / 2})`}
        textAnchor="middle"
      >
        CH {afterCH.toFixed(1)} m
      </text>
      {/* CBH dimension line (left) */}
      <line x1="26" y1={baseY} x2="26" y2={groundY} stroke="#ffb86b" strokeWidth="1.5" />
      <line x1="22" y1={baseY} x2="30" y2={baseY} stroke="#ffb86b" />
      <line x1="22" y1={groundY} x2="30" y2={groundY} stroke="#ffb86b" />
      <text
        x="18"
        y={(baseY + groundY) / 2}
        fill="#ffb86b"
        fontSize="10"
        transform={`rotate(-90 18 ${(baseY + groundY) / 2})`}
        textAnchor="middle"
      >
        CBH {afterCBH.toFixed(1)} m
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Stem-density strip — 10 stems, thinned ones reduced to stumps.
// ---------------------------------------------------------------------------
function StemStrip({ thinningPct }: { thinningPct: number }) {
  const total = 10;
  const removed = Math.round((Math.min(Math.max(thinningPct, 0), 100) / 100) * total);
  // Same scattered removal order as the 3D stand's hash-driven thinning feel.
  const order = [3, 7, 1, 8, 5, 0, 9, 4, 6, 2];
  const removedSet = new Set(order.slice(0, removed));
  return (
    <svg width="100%" viewBox="0 0 250 34" style={{ display: "block" }}>
      <line x1="6" y1="30" x2="244" y2="30" stroke="rgba(255,255,255,0.25)" />
      {Array.from({ length: total }, (_, i) => {
        const x = 16 + i * 24;
        return removedSet.has(i) ? (
          <rect key={i} x={x - 2.5} y={25} width={5} height={5} rx={1} fill="#8a6845" opacity={0.8} />
        ) : (
          <g key={i}>
            <rect x={x - 1.5} y={12} width={3} height={18} fill="#6b4a32" />
            <polygon points={`${x},2 ${x - 6},16 ${x + 6},16`} fill="#3f7a4a" />
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
const Readout = ({ label, value, unit }: { label: string; value: string; unit?: string }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: 0.6, borderBottom: `1px solid ${BORDER}` }}>
    <Typography sx={{ color: TEXT_3, fontSize: "0.74rem" }}>{label}</Typography>
    <Typography sx={{ fontSize: "0.8rem", color: ACCENT, fontWeight: 700 }}>
      {value}
      {unit ? ` ${unit}` : ""}
    </Typography>
  </Box>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT_3, mb: 1, mt: 0.5 }}>
    {children}
  </Typography>
);

const SilvicultureLabModal: React.FC<SilvicultureLabModalProps> = ({
  open,
  onClose,
  thinningPct,
  setThinningPct,
  surfaceFuelReductionPct,
  setSurfaceFuelReductionPct,
  removeSlash,
  setRemoveSlash,
  cbhRaiseM,
  setCbhRaiseM,
}) => {
  const [veg, setVeg] = useState<VegContext>("pine");
  const { shape, hasCBD } = VEG_CONTEXTS[veg];

  const { afterCH, afterCBH, afterCBD } = silvicultureResult(
    cbhRaiseM,
    thinningPct
  );
  const crownLen = Math.max(afterCH - afterCBH, 0);
  const crownRatio = afterCH > 0 ? Math.round((crownLen / afterCH) * 100) : 0;

  return (
    <Modal open={open} onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 13000 }}>
      <Box
        sx={{
          width: "min(1120px, 95vw)",
          maxHeight: "calc(var(--vh100) * 0.92)",
          bgcolor: "#1a1d2e",
          color: "white",
          borderRadius: 2,
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* title */}
        <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1.5 }}>
          <span style={{ fontSize: "1.1rem" }}>🌲</span>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>Silviculture Lab</Typography>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={veg}
            onChange={(_e, v) => v && setVeg(v)}
            sx={{
              "& .MuiToggleButton-root": { color: TEXT_2, borderColor: BORDER, textTransform: "none", px: 1.5 },
              "& .Mui-selected": { color: "#06281f !important", bgcolor: `${ACCENT} !important`, fontWeight: 700 },
            }}
          >
            {(Object.keys(VEG_CONTEXTS) as VegContext[]).map((k) => (
              <ToggleButton key={k} value={k}>
                {VEG_CONTEXTS[k].label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* body: 3 columns */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "300px 260px 1fr" },
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {/* controls */}
          <Box sx={{ p: 2.5, borderRight: { md: `1px solid ${BORDER}` } }}>
            <SectionLabel>Thinning</SectionLabel>
            <Typography sx={{ fontSize: "0.8rem", color: TEXT_2, display: "flex", justifyContent: "space-between" }}>
              Stems removed <b style={{ color: ACCENT }}>{thinningPct}%</b>
            </Typography>
            <Slider sx={sliderSx} size="small" min={0} max={100} step={5} value={thinningPct} onChange={(_e, v) => setThinningPct(v as number)} />
            <Typography sx={{ fontSize: "0.7rem", color: TEXT_3, mt: -0.5, mb: 1, lineHeight: 1.4 }}>
              {hasCBD
                ? "Removing stems reduces elevated fuel load and canopy bulk density (CBD) together."
                : `${VEG_CONTEXTS[veg].label} has no CBD attribute — thinning acts on fuel loads only.`}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: TEXT_2, display: "flex", justifyContent: "space-between", mt: 1 }}>
              Surface fuel reduction <b style={{ color: ACCENT }}>{surfaceFuelReductionPct}%</b>
            </Typography>
            <Slider sx={sliderSx} size="small" min={0} max={100} step={5} value={surfaceFuelReductionPct} onChange={(_e, v) => setSurfaceFuelReductionPct(v as number)} />
            <FormControlLabel
              sx={{ color: TEXT_2, mt: 0.5 }}
              control={<Switch checked={removeSlash} onChange={(e) => setRemoveSlash(e.target.checked)} sx={{ "& .Mui-checked": { color: ACCENT }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT } }} />}
              label={<span style={{ fontSize: "0.82rem" }}>Remove slash</span>}
            />

            <Divider sx={{ borderColor: BORDER, my: 2 }} />
            <SectionLabel>Canopy structure</SectionLabel>
            <Typography sx={{ fontSize: "0.8rem", color: TEXT_2, display: "flex", justifyContent: "space-between" }}>
              Raise canopy base — pruning <b style={{ color: ACCENT }}>+{cbhRaiseM.toFixed(1)} m</b>
            </Typography>
            <Slider sx={sliderSx} size="small" min={0} max={12} step={0.5} value={cbhRaiseM} onChange={(_e, v) => setCbhRaiseM(v as number)} />
          </Box>

          {/* cross-section */}
          <Box sx={{ p: 2, borderRight: { md: `1px solid ${BORDER}` }, background: "rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" }}>
            <SectionLabel>Annotated cross-section</SectionLabel>
            <CrossSection afterCH={afterCH} afterCBH={afterCBH} shape={shape} />
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: "0.66rem", color: TEXT_3, mb: 0.5 }}>
                Stand density — stems removed {thinningPct}%
              </Typography>
              <StemStrip thinningPct={thinningPct} />
            </Box>
          </Box>

          {/* live 3D + readouts */}
          <Box sx={{ p: 2.5, minWidth: 0 }}>
            <SectionLabel>Live stand</SectionLabel>
            <SilviculturePreview
              shape={shape}
              cbhRaiseM={cbhRaiseM}
              thinningPct={thinningPct}
              removeSlash={removeSlash}
              height={250}
            />
            <Box sx={{ mt: 1.5 }}>
              <SectionLabel>Treated stand (representative)</SectionLabel>
              <Readout label="Canopy height (CH)" value={afterCH.toFixed(1)} unit="m" />
              <Readout label="Canopy base height (CBH)" value={afterCBH.toFixed(1)} unit="m" />
              {hasCBD ? (
                <Readout label="Canopy bulk density (CBD)" value={afterCBD.toFixed(2)} unit="kg/m³" />
              ) : (
                <Readout label="Canopy bulk density (CBD)" value="n/a" />
              )}
              <Readout label="Crown length" value={crownLen.toFixed(1)} unit="m" />
              <Readout label="Crown ratio" value={`${crownRatio}%`} />
              <Readout label="Stems retained" value={`${100 - thinningPct}%`} />
            </Box>
            {afterCBH >= 2 ? (
              <Box sx={{ mt: 1.5, fontSize: "0.72rem", color: "#bfead7", background: "rgba(0,212,173,0.1)", border: "1px solid rgba(0,212,173,0.3)", borderRadius: 1, p: 1, lineHeight: 1.45 }}>
                ✓ Canopy base at {afterCBH.toFixed(1)} m — above the 2 m ladder-fuel threshold; suppresses torching / crown-fire initiation downstream.
              </Box>
            ) : (
              <Box sx={{ mt: 1.5, fontSize: "0.72rem", color: "#ffd9a8", background: "rgba(255,107,53,0.12)", border: "1px solid rgba(255,107,53,0.3)", borderRadius: 1, p: 1, lineHeight: 1.45 }}>
                ⚠ Canopy base still below 2 m — ladder fuel remains; raise CBH further to cut crown-fire initiation.
              </Box>
            )}
          </Box>
        </Box>

        {/* actions */}
        <Box sx={{ p: "12px 20px", borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} variant="contained" sx={{ bgcolor: ACCENT, color: "#06281f", fontWeight: 700, "&:hover": { bgcolor: "#00bfa0" } }}>
            Done
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SilvicultureLabModal;
